/**
 * ScreenTitle Component
 * Main heading component with consistent typography
 */

import React, { type ReactNode } from 'react';
import { designTokens } from '../../constants/designTokens';

interface ScreenTitleProps {
  children: ReactNode;
  level?: 1 | 2;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  desktopEnhanced?: boolean;
  className?: string;
}

export const ScreenTitle = React.memo(function ScreenTitle({
  children,
  level = 1,
  size,
  desktopEnhanced = true,
  className = '',
}: ScreenTitleProps) {
  const Tag = `h${level}` as 'h1' | 'h2';

  // Determine size based on level if not explicitly provided
  const titleSize = size || (level === 1 ? 'xl' : 'lg');

  return (
    <>
      <Tag
        className={`screen-title screen-title-h${level} screen-title-${titleSize} ${desktopEnhanced ? 'desktop-enhanced' : ''} ${className}`}
      >
        {children}
      </Tag>

      <style>{`
        .screen-title {
          color: ${designTokens.colors.text.primary};
          font-family: ${designTokens.typography.fontFamily.primary};
          font-weight: ${designTokens.typography.fontWeight.bold};
          line-height: ${designTokens.typography.lineHeight.tight};
          margin: 0;
          margin-bottom: ${designTokens.spacing.sm}; /* Standard 8px spacing */
          text-align: center;
        }
        
        /* Mobile-First Size Variants - Industry Standard */
        .screen-title-sm {
          font-size: ${designTokens.typography.fontSize.lg};     /* 18px */
        }
        
        .screen-title-md {
          font-size: ${designTokens.typography.fontSize.xl};     /* 20px */
        }
        
        .screen-title-lg {
          font-size: ${designTokens.typography.fontSize['2xl']}; /* 24px */
        }
        
        .screen-title-xl {
          font-size: ${designTokens.typography.fontSize['3xl']}; /* 28px - Default for main titles */
        }
        
        .screen-title-2xl {
          font-size: ${designTokens.typography.fontSize['4xl']}; /* 32px */
        }
        
        /* Mobile Specific - Default sizes work well, minimal adjustments needed */
        ${designTokens.media.mobile} {
          /* Mobile uses the base sizes defined above - no overrides needed */
          /* This ensures titles are 24-32px on mobile as per best practices */
        }
        
        
        /* Tablet (768px-1023px) - Slight scale up */
        ${designTokens.media.tablet} {
          .screen-title-lg {
            font-size: ${designTokens.typography.fontSize['3xl']}; /* 28px */
          }
          
          .screen-title-xl {
            font-size: ${designTokens.typography.fontSize['4xl']}; /* 32px */
          }
          
          .screen-title-2xl {
            font-size: ${designTokens.typography.fontSize['5xl']}; /* 36px */
          }
        }
        
        /* Desktop (1024px+) - Maximum sizes per industry standards */
        ${designTokens.media.desktop} {
          .screen-title-lg {
            font-size: ${designTokens.typography.fontSize['4xl']}; /* 32px */
          }
          
          .screen-title-xl {
            font-size: ${designTokens.typography.fontSize['5xl']}; /* 36px */
          }
          
          .screen-title-2xl {
            font-size: ${designTokens.typography.fontSize['6xl']}; /* 40px - max */
          }
        }
        
        
        /* Desktop Enhanced mode now follows standard sizing - no aggressive scaling */
        .screen-title.desktop-enhanced {
          /* Uses the same responsive sizes as defined above */
          /* This prevents over-scaling while maintaining the enhancement flag for compatibility */
        }
      `}</style>
    </>
  );
});
